<template>
  <div>
    <v-row class="mx-0" align="center">
      <v-col cols="12" sm="6" md="3" class="py-2">
        <v-text-field
          v-model="appSearch"
          append-icon="mdi-magnify"
          label="Search installed apks"
          single-line
          hide-details
          dense
          clearable
        />
      </v-col>

      <v-col
        v-if="statusKey"
        cols="12"
        sm="6"
        md="2"
        class="py-2"
      >
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          label="Status"
          hide-details
          dense
          clearable
        />
      </v-col>

      <v-col
        v-if="packageOptions.length"
        cols="12"
        sm="6"
        md="3"
        class="py-2"
      >
        <v-autocomplete
          v-model="packageFilter"
          :items="packageOptions"
          label="Package"
          hide-details
          dense
          clearable
        />
      </v-col>

      <v-col cols="12" sm="6" md="3" class="py-2">
        <v-select
          v-model="visibleColumns"
          :items="columnOptions"
          label="Columns"
          multiple
          hide-details
          dense
        >
          <template v-slot:selection="{ index }">
            <span v-if="index === 0" class="grey--text caption">
              {{ visibleColumns.length }} of {{ columnOptions.length }} shown
            </span>
          </template>
        </v-select>
      </v-col>

      <v-spacer />
      <v-col cols="auto" class="py-2">
        <v-btn small text color="primary" :loading="loading" @click="getDeviceApps">
          <v-icon small class="mr-1">mdi-refresh</v-icon> Refresh
        </v-btn>
      </v-col>
    </v-row>

    <v-alert
      v-if="loadError"
      dense
      text
      type="warning"
      class="mx-3"
    >
      {{ loadError }}
    </v-alert>

    <div class="apps-table">
      <v-data-table
        :headers="appsHeaders"
        :items="filteredApps"
        :search="appSearch"
        :loading="loading"
        :items-per-page="5"
        :footer-props="{
          showFirstLastPage: true,
          'items-per-page-options': [5, 10, 15, 25],
        }"
        no-data-text="No installed apks reported for this device"
      >
        <template
          v-for="header in appsHeaders"
          v-slot:[`item.${header.value}`]="{ item }"
        >
          <span :key="header.value">{{ formatCell(item[header.value]) }}</span>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
  // Preferred column order and labels. Only the keys actually present in the
  // response are rendered, so this survives the payload gaining or dropping
  // fields without showing empty columns.
  const PREFERRED_COLUMNS = [
    { key: 'applicationName', text: 'App' },
    { key: 'appName', text: 'App' },
    { key: 'label', text: 'App' },
    { key: 'name', text: 'App' },
    { key: 'packageName', text: 'Package' },
    { key: 'versionName', text: 'Version' },
    { key: 'versionCode', text: 'Version Code' },
    { key: 'installedVersionName', text: 'Installed Version' },
    { key: 'installedVersionCode', text: 'Installed Code' },
    { key: 'status', text: 'Status' },
    { key: 'installStatus', text: 'Status' },
    { key: 'source', text: 'Source' },
    { key: 'isSystemApp', text: 'System App' },
    { key: 'installedAt', text: 'Installed' },
    { key: 'firstInstalledAt', text: 'First Installed' },
    { key: 'lastUpdatedAt', text: 'Updated' },
    { key: 'updatedAt', text: 'Updated' },
    { key: 'reportedAt', text: 'Reported' },
    { key: 'lastSeenAt', text: 'Last Seen' },
    { key: 'lastStatus', text: 'Last Status' },
    { key: 'currentVersionInstalledAt', text: 'Current Version Installed' },
    { key: 'lastInstalledAt', text: 'Last Installed' },
    { key: 'previousVersionName', text: 'Previous Version' },
    { key: 'previousVersionCode', text: 'Previous Code' },
  ]

  // Shown by default; everything else is available from the Columns picker.
  const DEFAULT_COLUMNS = [
    'applicationName', 'appName', 'label', 'name',
    'packageName', 'versionName',
    'status', 'installStatus', 'lastStatus', 'reportedAt',
  ]

  // Candidate keys for the status filter, in preference order.
  const STATUS_KEYS = ['status', 'installStatus', 'lastStatus']

  // Query context, not worth a column of its own.
  const HIDDEN_KEYS = ['id', 'deviceId', 'companyId', 'macAddress', 'mobileStatusId', 'deviceName']

  const ISO_DATE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/

  export default {
    props: {
      currentMobileStatus: { type: [Number, String], default: 0 },
      device: { type: Object, default: () => ({}) },
    },

    data: () => ({
      deviceApps: [],
      appSearch: '',
      statusFilter: null,
      packageFilter: null,
      visibleColumns: [],
      loading: false,
      loadError: '',
    }),

    computed: {
      isSuperUser () {
        return this.$store.getters['authUser/isSuperUser']
      },

      // Columns are derived from the rows: preferred keys first in a sensible
      // order, then anything else the endpoint returned.
      allHeaders () {
        const present = new Set()
        this.deviceApps.forEach(app => {
          Object.keys(app || {}).forEach(key => present.add(key))
        })

        const headers = []
        const used = new Set()

        PREFERRED_COLUMNS.forEach(column => {
          if (present.has(column.key) && !used.has(column.text)) {
            headers.push({ text: column.text, value: column.key })
            used.add(column.text)
          }
        })

        const known = new Set(PREFERRED_COLUMNS.map(column => column.key))
        ;[...present]
          .filter(key => !known.has(key) && !HIDDEN_KEYS.includes(key))
          .forEach(key => headers.push({ text: this.humanize(key), value: key }))

        return headers.length ? headers : [{ text: 'Package', value: 'packageName' }]
      },

      appsHeaders () {
        const headers = !this.visibleColumns.length
          ? this.allHeaders
          : this.allHeaders.filter(header => this.visibleColumns.includes(header.value))

        return this.isSuperUser ? headers : headers.filter(header => header.value !== 'versionCode')
      },

      columnOptions () {
        return this.allHeaders
          .filter(header => this.isSuperUser || header.value !== 'versionCode')
          .map(header => ({ text: header.text, value: header.value }))
      },

      statusKey () {
        const present = new Set(this.allHeaders.map(header => header.value))
        return STATUS_KEYS.find(key => present.has(key)) || null
      },

      statusOptions () {
        if (!this.statusKey) {
          return []
        }
        const values = this.deviceApps
          .map(app => app[this.statusKey])
          .filter(value => value !== null && value !== undefined && value !== '')
        return [...new Set(values)].sort()
      },

      packageOptions () {
        const values = this.deviceApps
          .map(app => app.packageName)
          .filter(Boolean)
        return [...new Set(values)].sort()
      },

      filteredApps () {
        return this.deviceApps.filter(app => {
          if (this.statusFilter && this.statusKey && app[this.statusKey] !== this.statusFilter) {
            return false
          }
          if (this.packageFilter && app.packageName !== this.packageFilter) {
            return false
          }
          return true
        })
      },
    },

    watch: {
      currentMobileStatus () {
        this.getDeviceApps()
      },

      allHeaders (headers) {
        if (this.visibleColumns.length || !headers.length) {
          return
        }
        const defaults = headers
          .filter(header => DEFAULT_COLUMNS.includes(header.value))
          .map(header => header.value)

        this.visibleColumns = defaults.length ? defaults : headers.slice(0, 6).map(h => h.value)
      },
    },

    mounted () {
      this.getDeviceApps()
    },

    methods: {
      humanize (key) {
        const spaced = String(key)
          .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
          .replace(/[_-]+/g, ' ')
          .trim()
        return spaced.charAt(0).toUpperCase() + spaced.slice(1)
      },

      formatCell (value) {
        if (value === null || value === undefined || value === '') {
          return '-'
        }
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No'
        }
        if (typeof value === 'string' && ISO_DATE.test(value)) {
          return this.$format ? this.$format.dateTime(value) : value
        }
        if (typeof value === 'object') {
          return JSON.stringify(value)
        }
        return value
      },

      getDeviceApps () {
        const deviceId = this.currentMobileStatus || this.device.id
        const macAddress = this.device.macAddress

        if (!deviceId && !macAddress) {
          this.deviceApps = []
          return
        }

        this.loading = true
        this.loadError = ''
        this.statusFilter = null
        this.packageFilter = null

        const params = {}
        if (deviceId) params.deviceId = deviceId
        if (macAddress) params.macAddress = macAddress

        this.$http('AppInstallation/InstalledApks', { params })
          .then(response => {
            const data = response.data
            const rows = Array.isArray(data)
              ? data
              : (data && (data.items || data.apks || data.results || data.data)) || []

            this.deviceApps = Array.isArray(rows) ? rows : []
            this.loading = false
          })
          .catch(error => {
            const status = error && error.response ? error.response.status : null
            this.deviceApps = []
            this.loadError = status
              ? `Could not load installed apks (${status}).`
              : 'Could not load installed apks.'
            this.loading = false
          })
      },
    },
  }
</script>

<style scoped>
/* Vuetify's table wrapper already scrolls; without nowrap the columns squeeze
   and wrap instead of overflowing, so the scrollbar never appears. */
.apps-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.apps-table ::v-deep .v-data-table__wrapper th,
.apps-table ::v-deep .v-data-table__wrapper td {
  white-space: nowrap;
}
</style>
